
import React from 'react';
import { Pie, PieChart, Cell, Legend, ResponsiveContainer } from 'recharts';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { ChartContainer, ChartTooltip } from '@/components/ui/chart';
import { FileIcon } from 'lucide-react';
import { useIsMobile } from '@/hooks/use-mobile';
import { FILE_TYPE_COLORS } from './colors';

type FileTypeData = {
  name: string;
  value: number;
}

interface FileTypesChartProps {
  data: FileTypeData[];
}

const FileTypesChart: React.FC<FileTypesChartProps> = ({ data }) => {
  const isMobile = useIsMobile();

  return (
    <Card className="shadow-sm hover:shadow-md transition-all duration-300 border border-border/50">
      <CardHeader className="pb-2">
        <div className="flex items-center gap-2">
          <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary/10">
            <FileIcon className="h-4 w-4 text-primary" />
          </div>
          <CardTitle className="text-lg font-semibold">File Types</CardTitle>
        </div>
        <CardDescription>Most common attachment types</CardDescription>
      </CardHeader>
      <CardContent className="pt-0">
        {data.length > 0 ? (
          <div className="h-[300px] w-full">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart margin={{ top: 15, right: 15, bottom: 15, left: 15 }}>
                <Pie
                  data={data}
                  cx="50%"
                  cy="50%"
                  innerRadius={isMobile ? 40 : 60}
                  outerRadius={isMobile ? 70 : 90}
                  paddingAngle={3}
                  cornerRadius={3}
                  fill="#8884d8"
                  dataKey="value"
                  labelLine={false}
                  label={({ name, percent, value, cx, cy, midAngle, innerRadius, outerRadius, index }) => {
                    // Only show label for segments with larger percentages
                    if (percent < 0.08) return null;
                    
                    const RADIAN = Math.PI / 180;
                    const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
                    const x = cx + radius * Math.cos(-midAngle * RADIAN) * 1.3;
                    const y = cy + radius * Math.sin(-midAngle * RADIAN) * 1.3;
                    
                    return (
                      <text 
                        x={x} 
                        y={y} 
                        fill={FILE_TYPE_COLORS[index % FILE_TYPE_COLORS.length]}
                        textAnchor={x > cx ? 'start' : 'end'}
                        dominantBaseline="central"
                        className="font-semibold"
                        fontSize={isMobile ? 12 : 13}
                      >
                        {`${(percent * 100).toFixed(0)}%`}
                      </text>
                    );
                  }}
                >
                  {data.map((entry, index) => (
                    <Cell 
                      key={`cell-${index}`} 
                      fill={FILE_TYPE_COLORS[index % FILE_TYPE_COLORS.length]} 
                      stroke="#FFFFFF"
                      strokeWidth={2}
                      className="drop-shadow-sm hover:opacity-90 transition-opacity duration-200"
                    />
                  ))}
                </Pie>
                <ChartTooltip
                  content={({ active, payload }) => 
                    active && payload && payload.length ? (
                      <div className="rounded-lg border bg-background p-3 shadow-md">
                        <div className="flex items-center gap-2">
                          <div 
                            className="h-3 w-3 rounded-full" 
                            style={{ backgroundColor: payload[0].color }}
                          />
                          <span className="font-medium">{payload[0].name}</span>
                        </div>
                        <div className="mt-1 text-muted-foreground">
                          {payload[0].value} file{payload[0].value !== 1 ? 's' : ''} 
                          ({((Number(payload[0].value) / data.reduce((acc, curr) => acc + Number(curr.value), 0)) * 100).toFixed(1)}%)
                        </div>
                      </div>
                    ) : null
                  } 
                />
                <Legend
                  layout="horizontal"
                  verticalAlign="bottom"
                  align="center"
                  iconType="circle"
                  iconSize={10}
                  formatter={(value) => (
                    <span className="text-xs font-medium px-1">
                      {value}
                    </span>
                  )}
                  wrapperStyle={{
                    paddingTop: '20px',
                    fontSize: '12px',
                    width: '100%',
                    display: 'flex',
                    justifyContent: 'center',
                    flexWrap: 'wrap'
                  }}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
        ) : (
          <div className="flex items-center justify-center h-64 text-muted-foreground">
            No file type data available
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default FileTypesChart;
